<template>
  <div class="return-items grid-x large-padding-top-m large-padding-bottom-m padding-top-s padding-bottom-s border-top">
    <div class="small-7 medium-3 medium-padding-right-xxxl">
      <h3 id="return-items-heading">
        {{ config.returnItems.heading }}
      </h3>
    </div>
    <div class="small-5 medium-2 align-top padding-top-xxs text-right medium-text-left">
      <button v-if="!returnItemStore.returnItemsEditable.returnItems" id="change-return-items" class="link-body" @click="onChange">Change</button>
    </div>
    <div class="small-12 medium-7 small-margin-top-s medium-margin-top-z large-padding-left-m">
      <ReturnItem
        v-for="(product, index) in selectedItems"
        :key="product.key"
        :product="product"
        :is-hero="index === 0"
        :has-multiple-return-items="hasMultipleReturnItems"
        :has-upc-number="hasUpcNumber"
        @calculate-estimated-refund="calculateEstimatedRefund"
      />
      <div v-if="returnItemStore.returnItemsEditable.returnItems" class="small-12 medium-7">
        <button
          v-if="hasMultipleReturnItems"
          id="add-items-to-return"
          class="secondary button expanded margin-top-xs"
          type="button"
          tabindex="0"
          :disabled="shouldDisableAddToReturn || isPrepareDeclarationLoading"
          @click="showAddReturnItems"
        >
          {{ sliderConfig.addItemsToReturnButton }}
        </button>
        <AppButton
          id="continue-btn"
          class="primary expanded margin-top-s"
          :loading="isEstimatedRefundLoading || isPrepareDeclarationLoading"
          :disabled="!shouldEnableContinue"
          tabindex="0"
          @click="onContinue"
        >
          {{ change ? 'Save' : 'Continue' }}
        </AppButton>
      </div>
    </div>
  </div>
</template>
<script setup>
// imports
import { defineAsyncComponent, markRaw, ref, computed, watch, onMounted } from 'vue';
import { fireTag } from '@vue-util/tagManager';

// Local components
import AppButton from '@/components/AppButton.vue';
import ReturnItem from '@/components/returns/ReturnItem.vue';

// Utility functions and constants
import { buildKey } from '@/utils/returns/General';
import transformProducts from '@/utils/returns/TransformProducts';
import { createPayloadFromUopResponse } from '@/utils/returns/EstimatedRefund';

// Config
import * as config from 'config';

// Pinia Store & Composables
import { useSlideout } from '@/composables/useSlideout';
import useBrand from '@/composables/useBrand';
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';
import { useEstimatedRefundStore } from '@/stores/returns/useEstimatedRefundStore';
const returnItemStore = useReturnItemStore();
const estimatedRefundStore = useEstimatedRefundStore();

const { registerSlideout, toggleSlideout } = useSlideout();
const route = useRoute();
const { isMcom, isBcom } = useBrand();

// Local State
const change = ref(false);
const isEstimatedRefundLoading = ref(false);
const hasValueChanged = ref(false);
const overriddenContainsAreaRugItem = ref(false);
const exchangeOrReplaceThrottleTransition = ref(false);

// Computed Properties
const isPrepareDeclarationLoading = computed(() => returnItemStore.loading.prepareDeclarationResponse);
const hasUpcNumber = computed(() => !!route.query.upcnumber);
const hasMultipleReturnItems = computed(() => {
  if (!hasUpcNumber.value && returnItemStore.returnItems?.cartonLines?.length > returnItemStore.GET_SELECTED_RETURN_ITEMS.length) return true;
  return returnItemStore.returnItems?.cartonLines?.length > 1;
});
const shouldDisableAddToReturn = computed(() => {
  if (returnItemStore.isInvalidUpc) return true;
  if (returnItemStore.IS_MARKETPLACE_ORDER) {
    if (!hasUpcNumber.value && returnItemStore.returnItems?.cartonLines?.length > returnItemStore.GET_SELECTED_RETURN_ITEMS.length) return false;
    const currenSellerId = returnItemStore.GET_SELECTED_RETURN_ITEMS[0]?.itemDetails?.mkpLineItemDetails?.sellerId;
    const sameSellerItems = returnItemStore.returnItems?.cartonLines?.filter(
      (item) => item?.itemDetails?.mkpLineItemDetails?.sellerId === currenSellerId,
    );
    return returnItemStore.returnSelections.returnItemsDraft?.length >= sameSellerItems.length;
  }
  return returnItemStore.returnSelections.returnItemsDraft.length >= returnItemStore.returnItems?.cartonLines.length;
});
const shouldEnableContinue = computed(() => {
  if (!returnItemStore.returnSelections.returnItemsDraft.length) return false;
  if (returnItemStore.isInvalidUpc) return false;
  return returnItemStore.returnSelections.returnItemsDraft.reduce((acc, { returnType, returnReason, quantity }) => {
    if (acc && returnType !== 'PR') return true;
    if (!acc || !returnReason || !quantity) {
      return false;
    }
    return true;
  }, true);
});
const isOldPpeFlow = computed(() => returnItemStore.IS_MARKETPLACE_ORDER && !hasUpcNumber.value);
const isDifferentSellerItems = computed(() => {
  if (isOldPpeFlow.value) {
    const firstSellerId = returnItemStore.GET_SELECTED_RETURN_ITEMS[0]?.itemDetails.mkpLineItemDetails?.sellerId;
    return returnItemStore.GET_SELECTED_RETURN_ITEMS.some((item) => firstSellerId !== item.itemDetails.mkpLineItemDetails?.sellerId);
  }
  return false;
});
const selectedItems = computed(() =>
  transformProducts({
    page: 'returns',
    products: returnItemStore.GET_SELECTED_RETURN_ITEMS,
    isMkpOrder: returnItemStore.IS_MARKETPLACE_ORDER,
    inlineSizeAndColor: true,
  }),
);
const containsAreaRugItem = computed(() => {
  /**
   * If the last area rug item is removed and the item selections no longer contain any area rug items,
   * the getter will return false. Therefore, we need to check
   * If overriddenContainsAreaRugItem is not null, use it; otherwise, use the returnItemStore getter.
   * Based on this check, we will remove the previously selected return method and expand the return method section.
   */
  const containsAreaRug = overriddenContainsAreaRugItem.value || returnItemStore.CONTAINS_AREA_RUG_ITEM;
  if (isMcom) return containsAreaRug;
  if (isBcom && returnItemStore.returnSelections.returnMethod === config.RETURN_METHOD_KEY_MAP.storeReturn) return containsAreaRug;
  return false;
});
const sliderConfig = computed(() => {
  if (selectedItems.value.length > 0) {
    return {
      sliderTitle: 'Add items to return',
      buttonLabel: 'Add To Return',
      addItemsToReturnButton: 'Add More Items To Return',
    };
  }

  return {
    sliderTitle: 'Select items to start your return',
    buttonLabel: 'Start Return',
    addItemsToReturnButton: 'Add Items To Return',
  };
});

const shouldCallPrepareDeclaration = computed(() => {
  const isKSEnabledFor1P = returnItemStore.returnItems.isExchangeEnabled || returnItemStore.returnItems.isReplacementEnabled;

  if (!returnItemStore.IS_MARKETPLACE_ORDER && isKSEnabledFor1P) return true;

  if (returnItemStore.IS_MARKETPLACE_ORDER && returnItemStore.returnItems.isMkpV2RpReturnSubmitEnabled) return true;

  return false;
});

const showAddReturnItems = async () => {
  registerSlideout({
    id: 'item-details-slideout',
    title: sliderConfig.value.sliderTitle,
    props: {
      isOldPpeFlow: isOldPpeFlow.value,
      buttonLabel: sliderConfig.value.buttonLabel,
    },
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/AddItemsToReturn.vue'))),
  });

  toggleSlideout('item-details-slideout');
  // TODO: The firetag will vary based on the title.
  fireTag('link', {
    event_name: 'add items to return',
    site_action: 'add items to return start',
    action_location: 'order details',
  });
};

// TODO check if this calculation can be moved to a getter function.
const calculateEstimatedRefund = (initialPageLoad = false) => {
  let merchandiseTotal = 0.0;
  let protectionPlanTotal = 0.0;
  let totalTaxes = 0.0;
  let totalRefundAmount = 0.0;
  let itemsSelected = [];
  for (const item of returnItemStore.returnItems?.cartonLines || []) {
    for (const returnItem of returnItemStore.returnSelections.returnItemsDraft) {
      const key = buildKey(item);
      if (key === returnItem.key && item.returnType === 'PR') {
        itemsSelected.push({
          unitPrice: item.unitPrice,
          taxPerUnit: item.taxPerUnit,
          protectionPlanPrice: item.protectionPlanPrice,
          protectionPlanTax: item.protectionPlanTax,
          quantity: initialPageLoad ? item.returnQuantity : +returnItem.quantity,
          returnQuantity: item.returnQuantity,
        });
      }
    }
  }
  if (initialPageLoad) {
    itemsSelected = itemsSelected.filter((item) => item.returnQuantity === 1);
  }
  for (const item of itemsSelected) {
    if (!Number.isNaN(item.quantity)) {
      merchandiseTotal += +(item.unitPrice * item.quantity);
      totalTaxes += +(item.taxPerUnit * item.quantity);
      if (item.protectionPlanPrice) {
        protectionPlanTotal += +(item.protectionPlanPrice * item.quantity);
        totalTaxes += +(item.protectionPlanTax * item.quantity);
      }
    }
  }
  totalRefundAmount = merchandiseTotal + totalTaxes + protectionPlanTotal;
  estimatedRefundStore.SET_ESTIMATED_REFUND({
    merchandiseTotal,
    protectionPlanTotal,
    totalTaxes,
    totalRefundAmount,
    amountDue: 0,
  });
};

const getReturnReason = (code) => {
  const returnCodeItems = returnItemStore.returnItems?.cartonLines.filter((item) => item.returnReason?.code);

  const newCodes = returnCodeItems?.map((item) => item.returnReason) ?? [];
  const returnReasonValue = returnItemStore.returnItems?.returnReason;
  const updatedReturnReasons = [...(returnReasonValue ?? []), ...newCodes];
  const returnReason = updatedReturnReasons.find((reason) => reason?.code === code);
  return returnReason?.description;
};

const checkReturnMethodEligibility = () => {
  const blockedVendorItems = returnItemStore.GET_SELECTED_RETURN_ITEMS?.filter((item) => item.blockedVendorFlag);
  const hazmatItems = returnItemStore.GET_SELECTED_RETURN_ITEMS?.filter((item) => item.isHazmatItem);

  const isBlockedVendor = blockedVendorItems?.length !== 0;
  const isStoreReturn = returnItemStore.returnSelections.returnMethod === config.RETURN_METHOD_KEY_MAP.storeReturn;
  const isHazmat = hazmatItems?.length > 0;
  const isLabelFree = returnItemStore.returnSelections.returnMethod === config.RETURN_METHOD_KEY_MAP.labelFree;

  const isIneligibleBlockedVendor = isBlockedVendor && !isStoreReturn;
  const isIneligibleHazmat = isHazmat && isLabelFree;

  if (isIneligibleBlockedVendor || isIneligibleHazmat) {
    returnItemStore.SET_RETURN_METHOD_INELIGIBLE(true);
    returnItemStore.SET_RETURN_EDITABLE({
      returnItems: false,
      refundOptions: false,
      returnMethod: true,
    });
  } else {
    returnItemStore.SET_RETURN_EDITABLE({
      returnItems: false,
      refundOptions: false,
      returnMethod: false,
    });
  }
};

const callEstimatedRefund = async () => {
  try {
    isEstimatedRefundLoading.value = true;
    const payload = createPayloadFromUopResponse(
      returnItemStore.returnItems,
      returnItemStore.returnSelections,
      returnItemStore.returnSelections.returnMethod,
      returnItemStore.APPLY_CONDITIONAL_RETURN_FEE,
      returnItemStore.IS_REGISTRY_ORDER,
    );
    await estimatedRefundStore.GET_ESTIMATED_REFUND({ payload });
  } finally {
    isEstimatedRefundLoading.value = false;
  }
};

const submitPrepareDeclaration = async () => {
  const containsPRItems = returnItemStore.returnSelections.returnItems?.every((item) => item.returnType === 'PR');
  if (!containsPRItems) {
    await returnItemStore.SET_PREPARE_DECLARATION_RESPONSE({});
    return;
  }
  await returnItemStore.SUBMIT_PREPARE_DECLARATION();
};

/**
 * hasValueChanged becomes true from watcher when there is a change in the returnselection.retunrItmes.
 * If the user clicks on "Change" and then "Save" without making any modifications,
 * we skip calling submitPrepareDeclaration().
 */
const handlePrepareDeclaration = async () => {
  if (shouldCallPrepareDeclaration.value && hasValueChanged.value) {
    returnItemStore.SET_EXCHANGE_ITEM({ reset: true });
    returnItemStore.SET_RETURN_SELECTIONS({ refundOption: null, returnMethod: '', shippingAddressDetails: null });
    await submitPrepareDeclaration();
  }
};

const onContinue = async () => {
  returnItemStore.SET_ERRORS({ v2SumbitReturnError: null });
  returnItemStore.SET_RETURN_SELECTIONS({
    returnItems: returnItemStore.returnSelections.returnItemsDraft,
  });

  await handlePrepareDeclaration();
  if (returnItemStore.errors.prepareDeclarationResponse) return;

  if (isDifferentSellerItems.value) {
    returnItemStore.SET_HAS_ITEMS_FROM_DIFFERENT_SELLER(true);
  } else if (!returnItemStore.returnSelections.returnMethod) {
    if (
      (returnItemStore.returnSelections.refundOption?.choice === 'REPLACEMENT' && !returnItemStore.IS_REPLACE_ELIGIBLE) ||
      (returnItemStore.returnSelections.refundOption?.choice === 'EXCHANGE' && !returnItemStore.IS_EXCHANGE_ELIGIBLE)
    ) {
      returnItemStore.SET_RETURN_SELECTIONS({ refundOption: null });
    }
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(2);
    returnItemStore.SET_HAS_ITEMS_FROM_DIFFERENT_SELLER(false);
    returnItemStore.SET_RETURN_EDITABLE({ returnItems: false, refundOptions: true, returnMethod: false });
  } else if ((isBcom && returnItemStore.IS_MARKETPLACE_ORDER) || containsAreaRugItem.value) {
    returnItemStore.SET_RETURN_SELECTIONS({ returnMethod: '' });
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(3);
    returnItemStore.SET_RETURN_EDITABLE({ returnItems: false, refundOptions: false, returnMethod: true });
  } else if (exchangeOrReplaceThrottleTransition.value) {
    returnItemStore.SET_RETURN_SELECTIONS({ refundOption: null, returnMethod: '', shippingAddressDetails: null });
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(2);
    returnItemStore.SET_RETURN_EDITABLE({
      returnItems: false,
      refundOptions: true,
      shippingAddress: false,
      returnMethod: false,
    });
  } else {
    returnItemStore.SET_HAS_ITEMS_FROM_DIFFERENT_SELLER(false);
    checkReturnMethodEligibility();
    if (!returnItemStore.isReturnMethodIneligible) {
      await callEstimatedRefund();
    }
  }

  // Analytics
  const selectedValues = [];
  for (const item of returnItemStore.returnSelections.returnItems) {
    const selectedReturnReason = getReturnReason(item.returnReason);
    const selectedQuantity = item.quantity;
    selectedValues.push(`${selectedQuantity}|${selectedReturnReason}`);
  }
  fireTag('link', {
    event_name: 'items to return',
    site_action: 'items to return complete',
    action_location: 'order details',
    action_misc: selectedValues,
  });

  hasValueChanged.value = false;
};

const onChange = () => {
  change.value = true;
  if (returnItemStore.isReturnMethodIneligible && returnItemStore.returnSelections.returnMethod) {
    returnItemStore.SET_RETURN_METHOD_INELIGIBLE(false);
  }
  if (!returnItemStore.returnSelections.returnMethod) {
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(1);
  }

  returnItemStore.SET_RETURN_EDITABLE({ returnItems: true, refundOptions: false, returnMethod: false });
  fireTag('link', {
    link_name: 'change return items',
    event_name: 'change return items',
  });
};

const showSlideOut = () => {
  if (!hasUpcNumber.value && !returnItemStore.hasEmptyCartonLine && returnItemStore.returnItems?.cartonLines.length > 1) {
    showAddReturnItems();
  }
};

// Watchers
watch(
  () => returnItemStore.returnItemsEditable.returnItems,
  (sectionOpened) => {
    // If the user has updated the return items section but did not save the changes, revert to the original selections.
    let changeReturnSelections = false;
    if (!sectionOpened && returnItemStore.returnSelections.returnItems.length > 0 && returnItemStore.returnSelections.returnItemsDraft.length > 0) {
      if (returnItemStore.returnSelections.returnItems.length !== returnItemStore.returnSelections.returnItemsDraft.length) {
        changeReturnSelections = true;
      } else {
        for (const item of returnItemStore.returnSelections.returnItems) {
          const returnItem = returnItemStore.returnSelections.returnItemsDraft.find((itemDraft) => itemDraft.key === item.key);
          if (!returnItem || returnItem?.quantity !== item.quantity) {
            changeReturnSelections = true;
          }
        }
      }
    }
    if (changeReturnSelections) {
      returnItemStore.SET_RETURN_SELECTIONS({ returnItemsDraft: returnItemStore.returnSelections.returnItems });
      calculateEstimatedRefund(false);
    }
  },
);
watch(
  () => returnItemStore.GET_SELECTED_RETURN_ITEMS,
  (newVal) => {
    if (newVal && !newVal.length) showAddReturnItems();
  },
);
watch(
  () => returnItemStore.returnSelections.returnItems,
  () => {
    hasValueChanged.value = true;
  },
  { deep: true },
);
/**
 * If the returnItemStore getter becomes false and it was previously true, update the overridden value
 * Reset overriddenContainsAreaRugItem to false to use the returnItemStore getter value
 */
watch(
  () => returnItemStore.CONTAINS_AREA_RUG_ITEM,
  (newValue, oldValue) => {
    if (returnItemStore.returnSelections.returnMethod && !newValue && oldValue) {
      overriddenContainsAreaRugItem.value = oldValue;
    } else {
      overriddenContainsAreaRugItem.value = false;
    }
  },
);
watch(
  () => [returnItemStore.IS_EXCHANGE_ELIGIBLE, returnItemStore.IS_REPLACE_ELIGIBLE],
  () => {
    const hasReturnSelections = returnItemStore.returnSelections.returnMethod && returnItemStore.returnSelections.refundOption?.choice;
    if (hasReturnSelections) {
      exchangeOrReplaceThrottleTransition.value = true;
    } else {
      exchangeOrReplaceThrottleTransition.value = false;
    }
  },
);

// Lifecycle Hooks
onMounted(async () => {
  showSlideOut();
  if (hasUpcNumber.value) calculateEstimatedRefund(true);
});
</script>
